import type { Option } from "@tento-lona/sheets";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { LonaColors } from "@tento-lona/internal-ui";
import { Network } from "@tento-lona/network";
import type {
  AuthScopedCalendarDisplay,
  AuthScopedCalendarId,
  EventsClient,
} from "@tento-lona";
import { CalendarSwatch } from "@tento-ui/components/calendar-list/calendar-list-view";
import { UserCalendarList } from "./calendar-list.ts";

// The default (non-toggle) calendar indicator: a solid colour square. Adapter
// over the pure CalendarSwatch, resolving the calendar's colour from its colorId.
export namespace CalendarDefaultAccessory {
  export function makeWith(acal: AuthScopedCalendarDisplay): CalendarSwatch {
    const [_, cal] = acal;
    const color = cal.colorId
      ? LonaColors.fromColorId(Number.parseInt(cal.colorId))
      : null;
    return CalendarSwatch.makeWith({
      color: color ? `var(--${color})` : "var(--fg-icon-ter)",
    });
  }
}

@component({
  name: "lona-calendar-picker",
})
export class CalendarPicker extends LonaWebComponent {
  static makeWith(props: CalendarPicker.Props): CalendarPicker {
    return CalendarPicker.make().bind(props);
  }

  bind(props: CalendarPicker.Props): CalendarPicker {
    DomUtils.clearChildren(this.$("root"));
    const $calendars = UserCalendarList.makeWith(props.ucals, {
      ...(props.selected != null ? { selected: props.selected } : {}),
      onSelect: props.onSelect,
      accessoryFactory: CalendarDefaultAccessory.makeWith,
      showEditableOnly: props.showEditableOnly ?? false,
      ...(props.writability != null ? { writability: props.writability } : {}),
    });
    this.$("root").appendChild($calendars);
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      #root {
        max-height: 240px;
        overflow-y: scroll;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root"></std-col>
  `;
}
export namespace CalendarPicker {
  export type Props = {
    ucals: readonly Network.CalendarList[];
    selected?: AuthScopedCalendarId;
    onSelect: (acal: AuthScopedCalendarDisplay) => void;
    showEditableOnly?: boolean;
    /** With `showEditableOnly`, also leaves out a calendar this answers is read-only (`EventsClient.peekCalendarWritability`; `null`: unknown). */
    writability?: (calendar: AuthScopedCalendarDisplay) => Option<EventsClient.Writability>;
  };
}
